import {
  AspectRatio,
  Close,
  Crop,
  Done,
  RotateLeft,
  RotateRight,
  ZoomIn,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slider,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import Cropper, {Area, Size} from "react-easy-crop";
import {useDownBreakpoint} from "@hooks";
import {cropImage} from "./functions";

const arButton: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  flex: 1,
};

const DESKTOP_SIZE: Size = {width: 600, height: 500};
// margine attorno all'area di ritaglio, per poterla trascinare
const CROP_MARGIN = 32;

const CropDialog = ({
  open,
  file,
  onClose,
  onConfirm,
}: {
  open: boolean;
  file: {name: string; base64: string} | undefined;
  onConfirm: (file: File) => void;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const isDownSm = useDownBreakpoint("sm");

  const [imageSrc, setImageSrc] = useState<string>();
  const [crop, setCrop] = useState({x: 0, y: 0});
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState<number>();
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [rotation, setRotation] = useState(0);
  const [imageSize, setImageSize] = useState<Size>();
  const [cropSizePercentage, setCropPercentage] = useState(100);

  // su mobile l'area del cropper dipende dallo schermo: la si misura
  const cropperRef = useRef<HTMLDivElement | null>(null);
  const [cropperSize, setCropperSize] = useState<Size>(DESKTOP_SIZE);

  // Reset stato quando si chiude il dialog
  useEffect(() => {
    if (!open) {
      setCrop({x: 0, y: 0});
      setZoom(1);
      setRotation(0);
      setCropPercentage(100);
      setAspectRatio(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (file?.base64) {
      setImageSrc(file.base64);
      loadImageSize(file.base64);
    }
  }, [file?.base64]);

  useEffect(() => {
    const el = cropperRef.current;
    if (!open || !el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const {width, height} = entry.contentRect;
      if (width > 0 && height > 0) setCropperSize({width, height});
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, isDownSm]);

  const loadImageSize = (src: string) => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setImageSize({width: img.width, height: img.height});
      setAspectRatio(img.width / img.height);
    };
  };

  // l'area di ritaglio più grande che entra nel cropper, ridotta dallo slider
  const ratio = aspectRatio || 1;
  const maxWidth = Math.max(
    Math.min(
      cropperSize.width - CROP_MARGIN,
      (cropperSize.height - CROP_MARGIN) * ratio,
    ),
    1,
  );
  const cropWidth = maxWidth * (cropSizePercentage / 100);
  const cropSize: Size = {width: cropWidth, height: cropWidth / ratio};

  // Funzione per ottenere l"immagine ritagliata
  const handleDoneClick = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    const croppedImage = await cropImage(
      __filename,
      imageSrc,
      croppedAreaPixels,
      rotation,
    );
    onConfirm(croppedImage);
  };

  const handleCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const ratios = [
    {label: "16/9", value: 16 / 9},
    {label: "16/10", value: 16 / 10},
    {label: "4/3", value: 4 / 3},
    {label: "1/1", value: 1},
    ...(imageSize
      ? [{label: "-", value: imageSize.width / imageSize.height}]
      : []),
  ];

  const rotateButtons = (
    <>
      <IconButton onClick={() => setRotation(prev => prev - 90)}>
        <RotateLeft color="primary" />
      </IconButton>
      <IconButton onClick={() => setRotation(prev => prev + 90)}>
        <RotateRight color="primary" />
      </IconButton>
    </>
  );

  const ratioButtons = ratios.map(r => (
    <Button
      key={r.label}
      disabled={aspectRatio === r.value}
      onClick={() => setAspectRatio(r.value)}
      sx={arButton}>
      <Box
        sx={{
          border: "1px solid",
          borderRadius: 1,
          width: Math.min(20 * r.value, 40),
          height: Math.min(20, 40 / r.value),
        }}
      />
      <Typography variant="caption">{r.label}</Typography>
    </Button>
  ));

  const zoomSlider = (orientation: "vertical" | "horizontal") => (
    <Slider
      value={zoom}
      orientation={orientation}
      min={1}
      max={3}
      step={0.01}
      onChange={(_, newZoom) => setZoom(newZoom as number)}
    />
  );

  const cropSizeSlider = (orientation: "vertical" | "horizontal") => (
    <Slider
      value={cropSizePercentage}
      orientation={orientation}
      min={1}
      max={100}
      step={1}
      onChange={(_, percentage) => setCropPercentage(percentage as number)}
    />
  );

  const cropper = (
    <Cropper
      image={imageSrc}
      crop={crop}
      zoom={zoom}
      cropSize={cropSize}
      onCropChange={setCrop}
      onZoomChange={setZoom}
      rotation={rotation}
      onRotationChange={setRotation}
      onCropComplete={handleCropComplete}
    />
  );

  if (isDownSm) {
    return (
      <Dialog open={open} fullScreen onClose={onClose}>
        <Stack height="100%" bgcolor={theme.palette.background.paper}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p={1}>
            <IconButton onClick={onClose}>
              <Close color="primary" />
            </IconButton>
            <Stack direction="row">{rotateButtons}</Stack>
            <IconButton onClick={handleDoneClick}>
              <Done color="primary" />
            </IconButton>
          </Stack>
          <Box ref={cropperRef} position="relative" flex="1 1 0" minHeight={0}>
            {cropper}
          </Box>
          <Stack gap={1} px={3} py={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <ZoomIn fontSize="small" color="disabled" />
              {zoomSlider("horizontal")}
            </Stack>
            <Stack direction="row" alignItems="center" gap={2}>
              <Crop fontSize="small" color="disabled" />
              {cropSizeSlider("horizontal")}
            </Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              <AspectRatio fontSize="small" color="disabled" />
              <Stack direction="row" flex={1} overflow="auto">
                {ratioButtons}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={onClose}>
      <Stack direction="row" bgcolor={theme.palette.background.paper}>
        <Box
          ref={cropperRef}
          position="relative"
          margin={"0 auto"}
          width={DESKTOP_SIZE.width}
          height={DESKTOP_SIZE.height}>
          {cropper}
        </Box>
        <Stack
          sx={{
            height: DESKTOP_SIZE.height,
            background: theme.palette.background.paper,
          }}
          direction="column">
          <Stack direction="row" p={1} spacing={2} justifyContent="center">
            {rotateButtons}
          </Stack>
          <Stack direction="row" flex={1} justifyContent="center" p={1}>
            <Stack direction="column" gap={3} alignItems="center">
              {zoomSlider("vertical")}
              <Tooltip title="Zoom">
                <ZoomIn fontSize="small" color="disabled" />
              </Tooltip>
            </Stack>
            <Stack
              direction="column"
              alignItems="center"
              borderLeft={`1px solid ${theme.palette.divider}`}
              borderRight={`1px solid ${theme.palette.divider}`}
              mx={1}>
              {ratioButtons}
              <Tooltip title="Aspect ratio">
                <AspectRatio fontSize="small" color="disabled" />
              </Tooltip>
            </Stack>

            <Stack direction="column" gap={3} alignItems="center">
              {cropSizeSlider("vertical")}
              <Tooltip title="Crop area">
                <Crop fontSize="small" color="disabled" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            p={1}
            spacing={1}
            mt={2}
            justifyContent="center">
            <IconButton onClick={handleDoneClick}>
              <Done color="primary" />
            </IconButton>
            <IconButton onClick={onClose}>
              <Close color="primary" />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export {CropDialog};
